import { Check, ChevronsUpDown, X } from "lucide-react";
import { useState } from "react";
import { useQuery } from "urql";

import { Button } from "@/components/ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { graphql } from "@/gen/gql";
import { cn, cx } from "@/utils/cx";
import { getProviderDisplayName, getProviderIcon } from "@/utils/streaming-channel";

const streamingChannelQuery = graphql(`
    query StreamingChannelById($id: UUID!) {
        streamingChannel(id: $id) {
            id
            externalChannelHandle
            externalChannelId
            provider
        }
    }
`);

const streamingChannelsQuery = graphql(`
    query AllStreamingChannels {
        streamingChannels {
            id
            externalChannelHandle
            externalChannelId
            provider
        }
    }
`);

function useStreamingChannelId(channelId: string | null | undefined) {
    const [result] = useQuery({
        query: streamingChannelQuery,
        variables: {
            id: channelId!,
        },
        pause: !channelId || channelId === "",
        requestPolicy: "cache-and-network",
    });

    return channelId && channelId !== "" ? result.data?.streamingChannel : null;
}

function useStreamingChannels() {
    const [result] = useQuery({
        query: streamingChannelsQuery,
        requestPolicy: "cache-and-network",
    });

    return {
        channels: result.data?.streamingChannels ?? [],
        loading: result.fetching,
        error: result.error,
    };
}

export const StreamingChannelIdInput = ({
    streamingChannelId,
    onStreamingChannelIdChange,
    className,
}: {
    streamingChannelId: string | null | undefined;
    onStreamingChannelIdChange: (channelId: string | null) => void;
    className?: string;
}) => {
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    const selectedChannel = useStreamingChannelId(streamingChannelId);
    const { channels, loading } = useStreamingChannels();

    const filteredChannels = channels.filter(channel => {
        const query = searchQuery.toLowerCase();
        return (
            channel.externalChannelHandle.toLowerCase().includes(query) ||
            channel.externalChannelId.toLowerCase().includes(query) ||
            channel.id.toLowerCase().includes(query)
        );
    });

    const clearInput = () => {
        onStreamingChannelIdChange(null);
        setSearchQuery("");
    };

    const getChannelDisplayText = (channel: typeof selectedChannel) => {
        if (!channel) return "Select streaming channel...";
        const providerName = getProviderDisplayName(channel.provider);
        return `${providerName} - ${channel.externalChannelHandle}`;
    };

    return (
        <div className={cx("flex flex-row gap-1", className)}>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="flex-1 justify-between"
                    >
                        <span className="flex items-center gap-2 truncate">
                            {selectedChannel && (
                                <>
                                    {(() => {
                                        const Icon = getProviderIcon(selectedChannel.provider);
                                        return <Icon className="h-4 w-4 shrink-0" />;
                                    })()}
                                </>
                            )}
                            <span className="truncate">
                                {getChannelDisplayText(selectedChannel)}
                            </span>
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0">
                    <Command shouldFilter={false}>
                        <CommandInput
                            placeholder="Search streaming channels..."
                            value={searchQuery}
                            onValueChange={setSearchQuery}
                        />
                        <CommandList>
                            {loading ? (
                                <CommandEmpty>Loading channels...</CommandEmpty>
                            ) : filteredChannels.length === 0 ? (
                                <CommandEmpty>No matching channels found.</CommandEmpty>
                            ) : (
                                <CommandGroup>
                                    {filteredChannels.map(channel => {
                                        const Icon = getProviderIcon(channel.provider);
                                        const providerName = getProviderDisplayName(
                                            channel.provider
                                        );
                                        return (
                                            <CommandItem
                                                key={channel.id}
                                                value={channel.id}
                                                onSelect={currentValue => {
                                                    onStreamingChannelIdChange(
                                                        streamingChannelId === currentValue
                                                            ? null
                                                            : currentValue
                                                    );
                                                    setOpen(false);
                                                    setSearchQuery("");
                                                }}
                                            >
                                                <Check
                                                    className={cn(
                                                        "mr-2 h-4 w-4",
                                                        streamingChannelId === channel.id
                                                            ? "opacity-100"
                                                            : "opacity-0"
                                                    )}
                                                />
                                                <div className="flex min-w-0 flex-1 items-center gap-2">
                                                    <Icon className="h-4 w-4 shrink-0" />
                                                    <div className="flex min-w-0 flex-col">
                                                        <div className="truncate font-medium">
                                                            {channel.externalChannelHandle}
                                                        </div>
                                                        <div className="text-muted-foreground truncate text-xs">
                                                            {providerName} •{" "}
                                                            {channel.externalChannelId}
                                                        </div>
                                                    </div>
                                                </div>
                                            </CommandItem>
                                        );
                                    })}
                                </CommandGroup>
                            )}
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
            {streamingChannelId && streamingChannelId !== "" && (
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-10 px-2"
                    onClick={clearInput}
                    aria-label="Clear selected channel"
                >
                    <X className="h-4 w-4" />
                </Button>
            )}
        </div>
    );
};
